<template>
  <Teleport to="body">
  <Transition
    enter-active-class="transition-opacity duration-300"
    enter-from-class="opacity-0"
    enter-to-class="opacity-100"
    leave-active-class="transition-opacity duration-300"
    leave-from-class="opacity-100"
    leave-to-class="opacity-0"
  >
    <div
      v-if="show"
      class="fixed inset-0 z-[10050] overflow-hidden"
    >
      <div
        class="absolute inset-0 bg-black bg-opacity-50"
        @click="handleOverlayClick"
      />
      <div class="relative flex h-full items-center justify-center p-4 pointer-events-none">
        <div
          :class="[
            'pointer-events-auto bg-white rounded-lg shadow-xl w-full max-h-[calc(100vh-2rem)] flex flex-col transform transition-all',
            maxWidthClass,
          ]"
        >
        <!-- Header -->
        <div class="flex-shrink-0 px-6 py-4 border-b border-gray-200">
          <h3 class="text-lg font-semibold text-black">
            {{ title }}
          </h3>
        </div>

        <!-- Body -->
        <div class="flex-1 min-h-0 overflow-y-auto overscroll-contain px-6 py-5">
          <div
            class="text-sm leading-relaxed mb-4 flex items-start gap-3"
          >
            <i
              v-if="showAttentionIcon"
              class="fas fa-exclamation-triangle text-amber-500 text-xl mt-0.5 flex-shrink-0"
            ></i>
            <div class="flex-1 min-w-0">
              <p
                v-if="attention"
                class="text-black whitespace-pre-line"
              >
                {{ attention }}
              </p>
              <p
                v-else
                class="text-black whitespace-pre-line"
              >
                {{ message }}
              </p>
              <p
                v-if="reminder"
                class="text-red-600 mt-3 whitespace-pre-line"
              >
                {{ reminder }}
              </p>
              <p
                v-if="attention && message"
                class="text-black mt-3 whitespace-pre-line"
              >
                {{ message }}
              </p>
            </div>
          </div>
          <div v-if="references && references.length > 0" class="mt-4">
            <p class="text-sm font-medium text-black mb-2">
              Produits concernés ({{ references.length }}):
            </p>
            <div
              class="max-h-60 overflow-y-auto overscroll-contain border border-gray-200 rounded-md p-3 bg-gray-50"
            >
              <ul class="space-y-1">
                <li
                  v-for="(ref, index) in references"
                  :key="index"
                  class="text-sm text-black flex items-start justify-between gap-3"
                >
                  <span class="min-w-0 break-words">
                    • {{ referenceLabel(ref) }}
                  </span>
                  <span
                    v-if="referenceMeta(ref)"
                    class="shrink-0 text-gray-600 whitespace-nowrap"
                  >
                    {{ referenceMeta(ref) }}
                  </span>
                </li>
              </ul>
            </div>
          </div>
          <div v-if="$slots.extra" class="mt-5">
            <slot name="extra" />
          </div>
          <div
            v-if="confirmLoading"
            class="mt-4 flex items-start gap-3 rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800"
          >
            <i class="fas fa-spinner fa-spin mt-0.5 shrink-0"></i>
            <span class="whitespace-pre-line">
              {{ progressLabel || "Traitement en cours, merci de patienter…" }}
            </span>
          </div>
        </div>

        <!-- Footer -->
        <div
          class="flex-shrink-0 px-6 py-4 bg-gray-50 rounded-b-lg flex justify-end space-x-3 border-t border-gray-200"
        >
          <button
            type="button"
            :disabled="confirmLoading"
            class="px-4 py-2 text-sm font-medium text-black bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            @click="handleCancel"
          >
            Annuler
          </button>
          <button
            type="button"
            :disabled="confirmDisabled || confirmLoading"
            :class="confirmButtonClass"
            class="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            @click="handleConfirm"
          >
            <i v-if="confirmLoading" class="fas fa-spinner fa-spin"></i>
            {{ confirmLoading ? loadingText : confirmText }}
          </button>
        </div>
        </div>
      </div>
    </div>
  </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useBodyScrollLock } from "@/composables/useBodyScrollLock";

interface ConfirmModalReference {
  label: string;
  meta?: string;
}

interface Props {
  show: boolean;
  title: string;
  message: string;
  attention?: string;
  reminder?: string;
  confirmText?: string;
  confirmButtonClass?: string;
  references?: Array<string | ConfirmModalReference>;
  confirmDisabled?: boolean;
  confirmLoading?: boolean;
  progressLabel?: string;
  loadingText?: string;
  showAttentionIcon?: boolean;
  lockBackgroundScroll?: boolean;
  maxWidthClass?: string;
}

interface Emits {
  (e: "confirm"): void;
  (e: "cancel"): void;
}

const props = withDefaults(defineProps<Props>(), {
  confirmText: "Confirmer",
  confirmButtonClass: "bg-blue-600 hover:bg-blue-700 focus:ring-blue-500",
  confirmDisabled: false,
  confirmLoading: false,
  progressLabel: "",
  loadingText: "Traitement en cours…",
  showAttentionIcon: false,
  lockBackgroundScroll: false,
  maxWidthClass: "max-w-md",
});

const emit = defineEmits<Emits>();

const shouldLockBackgroundScroll = computed(
  () => props.show && props.lockBackgroundScroll,
);
useBodyScrollLock(shouldLockBackgroundScroll);

const handleConfirm = () => {
  if (props.confirmLoading) return;
  emit("confirm");
};

const handleCancel = () => {
  if (props.confirmLoading) return;
  emit("cancel");
};

const handleOverlayClick = () => {
  handleCancel();
};

function referenceLabel(ref: string | ConfirmModalReference): string {
  return typeof ref === "string" ? ref : ref.label;
}

function referenceMeta(ref: string | ConfirmModalReference): string {
  return typeof ref === "string" ? "" : ref.meta?.trim() || "";
}
</script>
